import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { policiesApi } from '@/api/clients/policiesApi'
import type { Rule, Action } from '@/api/types'
import { Modal } from '@/components/common/Modal'
import { Button, Field, TextInput, TextArea, SelectInput } from '@/components/common/ui'
import {
  ACTIONS,
  actionDef,
  BANDS,
  CEL_FIELDS,
  CEL_OPS,
  celToPredicates,
  predicatesToCel,
  ruleToYaml,
  yamlToRule,
  type Predicate,
} from './ruleCel'

type Mode = 'builder' | 'yaml'

export function RuleEditor({
  orgId,
  pid,
  rule,
  onClose,
  onSaved,
}: {
  orgId: string
  pid: string
  rule: Rule | null
  onClose: () => void
  onSaved: () => void
}) {
  const initialPreds = rule ? celToPredicates(rule.when_expr) : []
  const [mode, setMode] = useState<Mode>('builder')
  const [ruleId, setRuleId] = useState(rule?.rule_id ?? '')
  const [priority, setPriority] = useState(rule?.priority ?? 100)
  const [frameworks, setFrameworks] = useState((rule?.framework_ref ?? []).join(', '))
  const [effFrom, setEffFrom] = useState(rule?.effective_from?.slice(0, 10) ?? '')
  const [effTo, setEffTo] = useState(rule?.effective_to?.slice(0, 10) ?? '')
  const [advanced, setAdvanced] = useState(initialPreds === null)
  const [rawWhen, setRawWhen] = useState(rule?.when_expr ?? '')
  const [preds, setPreds] = useState<Predicate[]>(initialPreds ?? [])
  const [actions, setActions] = useState<Action[]>(
    rule?.then_actions?.length
      ? rule.then_actions
      : [{ action: 'force_band', params: { band: 'critical' } }]
  )
  const [yamlText, setYamlText] = useState('')
  const [rationale, setRationale] = useState('')
  const [err, setErr] = useState('')

  const buildRule = (): Rule => ({
    rule_id: ruleId,
    priority,
    framework_ref: frameworks.split(',').map((s) => s.trim()).filter(Boolean),
    when_expr: advanced ? rawWhen.trim() : predicatesToCel(preds),
    then_actions: actions.filter((a) => a.action),
    effective_from: effFrom || null,
    effective_to: effTo || null,
  })

  const applyRule = (r: Rule) => {
    setRuleId(r.rule_id)
    setPriority(r.priority)
    setFrameworks((r.framework_ref ?? []).join(', '))
    setEffFrom(r.effective_from?.slice(0, 10) ?? '')
    setEffTo(r.effective_to?.slice(0, 10) ?? '')
    setActions(r.then_actions?.length ? r.then_actions : [])
    const p = celToPredicates(r.when_expr)
    if (p === null) {
      setAdvanced(true)
      setRawWhen(r.when_expr)
    } else {
      setAdvanced(false)
      setPreds(p)
    }
  }

  const switchMode = (next: Mode) => {
    setErr('')
    if (next === 'yaml') {
      setYamlText(ruleToYaml(buildRule()))
      setMode('yaml')
    } else {
      try {
        applyRule(yamlToRule(yamlText))
        setMode('builder')
      } catch (e) {
        setErr(`YAML: ${(e as Error).message}`)
      }
    }
  }

  const save = useMutation({
    mutationFn: () => {
      let finalRule: Rule
      if (mode === 'yaml') {
        finalRule = yamlToRule(yamlText) // throws → surfaced by onError
      } else {
        finalRule = buildRule()
      }
      if (!finalRule.rule_id) throw new Error('rule id is required')
      if (!finalRule.when_expr) throw new Error('a condition (when) is required')
      const body: Rule = { ...finalRule, rule_id: rule ? rule.rule_id : finalRule.rule_id }
      return rule
        ? policiesApi.updateRule(orgId, pid, rule.rule_id, body, rationale)
        : policiesApi.addRule(orgId, pid, body, rationale)
    },
    onSuccess: onSaved,
    onError: (e) => setErr((e as Error).message),
  })

  return (
    <Modal
      title={rule ? `Edit rule ${rule.rule_id}` : 'Add rule'}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={rationale.length < 20 || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save rule'}
          </Button>
        </>
      }
    >
      {/* Mode toggle */}
      <div className="mb-4 inline-flex rounded-lg border border-border-light dark:border-border-dark p-0.5">
        {(['builder', 'yaml'] as Mode[]).map((m) => (
          <button
            key={m}
            onClick={() => (m === mode ? undefined : switchMode(m))}
            className={`px-3 py-1 text-sm rounded-md ${
              mode === m
                ? 'bg-primary text-white'
                : 'text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
            }`}
          >
            {m === 'builder' ? 'Builder' : 'YAML'}
          </button>
        ))}
      </div>

      {mode === 'builder' ? (
        <BuilderMode
          ruleId={ruleId}
          setRuleId={setRuleId}
          isEdit={!!rule}
          priority={priority}
          setPriority={setPriority}
          frameworks={frameworks}
          setFrameworks={setFrameworks}
          effFrom={effFrom}
          setEffFrom={setEffFrom}
          effTo={effTo}
          setEffTo={setEffTo}
          advanced={advanced}
          setAdvanced={setAdvanced}
          rawWhen={rawWhen}
          setRawWhen={setRawWhen}
          preds={preds}
          setPreds={setPreds}
          actions={actions}
          setActions={setActions}
        />
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-subtle-light dark:text-subtle-dark">
            Full rule as YAML (id · priority · framework_ref · when · then · effective dates).
          </p>
          <textarea
            value={yamlText}
            onChange={(e) => setYamlText(e.target.value)}
            rows={16}
            spellCheck={false}
            className="w-full rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark px-3 py-2 font-mono text-xs text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
      )}

      <div className="mt-4">
        <Field label="Rationale (≥ 20 chars, audit)">
          <TextArea rows={2} value={rationale} onChange={(e) => setRationale(e.target.value)} />
        </Field>
      </div>
      {(err || save.error) && (
        <p className="mt-2 text-sm text-error">{err || (save.error as Error).message}</p>
      )}
    </Modal>
  )
}

function BuilderMode(props: {
  ruleId: string
  setRuleId: (v: string) => void
  isEdit: boolean
  priority: number
  setPriority: (v: number) => void
  frameworks: string
  setFrameworks: (v: string) => void
  effFrom: string
  setEffFrom: (v: string) => void
  effTo: string
  setEffTo: (v: string) => void
  advanced: boolean
  setAdvanced: (v: boolean) => void
  rawWhen: string
  setRawWhen: (v: string) => void
  preds: Predicate[]
  setPreds: (v: Predicate[]) => void
  actions: Action[]
  setActions: (v: Action[]) => void
}) {
  const {
    ruleId, setRuleId, isEdit, priority, setPriority, frameworks, setFrameworks,
    effFrom, setEffFrom, effTo, setEffTo, advanced, setAdvanced, rawWhen, setRawWhen,
    preds, setPreds, actions, setActions,
  } = props

  const setPred = (i: number, patch: Partial<Predicate>) =>
    setPreds(preds.map((p, j) => (j === i ? { ...p, ...patch } : p)))
  const setAct = (i: number, patch: Partial<Action>) =>
    setActions(actions.map((a, j) => (j === i ? { ...a, ...patch } : a)))

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Rule ID">
          <TextInput value={ruleId} onChange={(e) => setRuleId(e.target.value)} disabled={isEdit} />
        </Field>
        <Field label="Priority">
          <TextInput type="number" value={priority} onChange={(e) => setPriority(Number(e.target.value))} />
        </Field>
      </div>
      <Field label="Framework refs (comma-separated)">
        <TextInput value={frameworks} onChange={(e) => setFrameworks(e.target.value)} placeholder="cnsa_2_0, nist:fips_203" />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Effective from">
          <TextInput type="date" value={effFrom} onChange={(e) => setEffFrom(e.target.value)} />
        </Field>
        <Field label="Effective to">
          <TextInput type="date" value={effTo} onChange={(e) => setEffTo(e.target.value)} />
        </Field>
      </div>

      {/* Condition */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-medium text-text-light dark:text-text-dark">When (all must match)</p>
          <label className="flex items-center gap-1.5 text-xs text-subtle-light dark:text-subtle-dark">
            <input type="checkbox" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} />
            Advanced CEL
          </label>
        </div>
        {advanced ? (
          <TextArea
            rows={2}
            value={rawWhen}
            onChange={(e) => setRawWhen(e.target.value)}
            placeholder='asset.algorithm == "RSA" && daysUntil(asset.cert_expiry) < 0'
          />
        ) : (
          <div className="space-y-2">
            {preds.map((p, i) => {
              const def = CEL_FIELDS.find((f) => f.path === p.field)
              return (
                <div key={i} className="flex items-center gap-2">
                  <SelectInput value={p.field} onChange={(e) => setPred(i, { field: e.target.value })}>
                    <option value="">field…</option>
                    {CEL_FIELDS.map((f) => (
                      <option key={f.path} value={f.path}>{f.path}</option>
                    ))}
                  </SelectInput>
                  <SelectInput value={p.op} onChange={(e) => setPred(i, { op: e.target.value })} className="!w-20">
                    {CEL_OPS.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </SelectInput>
                  {def?.kind === 'enum' ? (
                    <SelectInput value={p.value} onChange={(e) => setPred(i, { value: e.target.value })}>
                      <option value="">value…</option>
                      {def.options!.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </SelectInput>
                  ) : (
                    <TextInput
                      type={def?.kind === 'number' ? 'number' : 'text'}
                      value={p.value}
                      onChange={(e) => setPred(i, { value: e.target.value })}
                      placeholder="value"
                    />
                  )}
                  <Button variant="ghost" icon="close" onClick={() => setPreds(preds.filter((_, j) => j !== i))} />
                </div>
              )
            })}
            <Button
              variant="ghost"
              icon="add"
              onClick={() => setPreds([...preds, { field: '', op: '==', value: '' }])}
            >
              Add condition
            </Button>
          </div>
        )}
      </div>

      {/* Actions */}
      <div>
        <p className="text-sm font-medium text-text-light dark:text-text-dark mb-1">Then (actions)</p>
        <div className="space-y-2">
          {actions.map((a, i) => {
            const def = actionDef(a.action)
            const val = (a.params?.[def.key] as string | number | undefined) ?? ''
            const setParam = (v: string | number) => setAct(i, { params: { [def.key]: v } })
            return (
              <div key={i} className="flex items-center gap-2">
                <SelectInput
                  value={a.action}
                  onChange={(e) => setAct(i, { action: e.target.value, params: {} })}
                >
                  {['Scoring', 'Evidence-only'].map((g) => (
                    <optgroup key={g} label={g}>
                      {ACTIONS.filter((d) => d.group === g).map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </SelectInput>
                {def.param === 'band' ? (
                  <SelectInput value={String(val)} onChange={(e) => setParam(e.target.value)}>
                    <option value="">band…</option>
                    {BANDS.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </SelectInput>
                ) : def.param === 'number' ? (
                  <TextInput type="number" value={val} onChange={(e) => setParam(Number(e.target.value))} />
                ) : def.param === 'date' ? (
                  <TextInput type="date" value={String(val)} onChange={(e) => setParam(e.target.value)} />
                ) : (
                  <TextInput
                    value={String(val)}
                    onChange={(e) => setParam(e.target.value)}
                    placeholder={def.key}
                  />
                )}
                <Button variant="ghost" icon="close" onClick={() => setActions(actions.filter((_, j) => j !== i))} />
              </div>
            )
          })}
          <Button
            variant="ghost"
            icon="add"
            onClick={() => setActions([...actions, { action: 'force_band', params: { band: 'critical' } }])}
          >
            Add action
          </Button>
        </div>
      </div>
    </div>
  )
}
